/**
 * Service Status Component
 * 
 * Monitors and displays the status of the reporting microservice
 * Provides fallback UI when the service is unavailable
 */

import React, { useState, useEffect, useRef } from 'react';
import { Alert, Button, Badge, Spinner } from 'react-bootstrap';
import { 
    BsWifiOff, 
    BsExclamationTriangle, 
    BsCheckCircle,
    BsArrowClockwise 
} from 'react-icons/bs';
import reportingService from '../../../services/reportingService';

const ServiceStatus = ({ 
    children, 
    fallbackComponent = null, 
    showStatus = true,
    autoRetry = false, // Disable auto retry by default to prevent spam
    retryInterval = 300000 // 5 minutes instead of 30 seconds
}) => {
    const [serviceStatus, setServiceStatus] = useState('checking');
    const [lastCheck, setLastCheck] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    
    // Use refs for stable references to avoid dependency issues
    const intervalRef = useRef(null);
    const retryCountRef = useRef(0);
    const serviceStatusRef = useRef('checking');

    const checkServiceHealth = async () => {
        try {
            setServiceStatus('checking');
            // Try to get system stats as a health check
            const response = await reportingService.getSystemStats();
            
            // Check if the response is successful - handle multiple formats
            // 1. Standard wrapped format: { success: true, data: {...} }
            // 2. Direct data format: { period: "7d", total_reports: 150, ... }
            // 3. Mixed format: response has success flag or actual data content
            if (response && (
                response.success === true || 
                response.data || 
                response.period ||
                response.total_reports !== undefined ||
                response.total_users !== undefined
            )) {
                setServiceStatus('online');
                serviceStatusRef.current = 'online';
                setRetryCount(0);
                retryCountRef.current = 0;
            } else {
                setServiceStatus('offline');
                serviceStatusRef.current = 'offline';
            }
        } catch (error) {
            setServiceStatus('offline');
            serviceStatusRef.current = 'offline';
            setRetryCount(prev => {
                const newCount = prev + 1;
                retryCountRef.current = newCount;
                return newCount;
            });
        } finally {
            setLastCheck(new Date());
        }
    };

    useEffect(() => {
        checkServiceHealth();

        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        if (autoRetry) {
            intervalRef.current = setInterval(() => {
                // Only retry if service is offline and we haven't exceeded reasonable retry limits
                // Use refs to avoid state dependencies
                if (retryCountRef.current >= 5) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    return;
                }
                
                // Check current status via ref to avoid state dependency
                if (serviceStatusRef.current === 'offline') {
                    checkServiceHealth();
                }
            }, retryInterval);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [autoRetry, retryInterval]); // Removed serviceStatus and retryCount dependencies

    const getStatusIcon = () => {
        switch (serviceStatus) {
            case 'checking':
                return <Spinner animation="border" size="sm" className="text-primary" />;
            case 'online':
                return <BsCheckCircle className="text-success" />;
            case 'offline':
            default:
                return <BsWifiOff className="text-danger" />;
        }
    };

    const getStatusBadge = () => {
        switch (serviceStatus) {
            case 'checking':
                return <Badge bg="secondary">Checking...</Badge>;
            case 'online':
                return <Badge bg="success">Online</Badge>;
            case 'offline':
            default:
                return <Badge bg="danger">Offline</Badge>;
        }
    };

    const ServiceOfflineAlert = () => (
        <Alert variant="warning" className="border-warning">
            <div className="d-flex align-items-center">
                <BsExclamationTriangle className="text-warning me-3 h4 mb-0" />
                <div className="flex-grow-1">
                    <Alert.Heading className="h6 mb-2">Reporting Service Unavailable</Alert.Heading>
                    <p className="mb-2">
                        The reporting service is currently unavailable. Some reporting features may not work as expected.
                    </p>
                    <div className="d-flex align-items-center text-muted small">
                        <span className="me-3">Status: {getStatusBadge()}</span>
                        {lastCheck && (
                            <span className="me-3">
                                Last checked: {lastCheck.toLocaleTimeString()}
                            </span>
                        )}
                        {retryCount > 0 && (
                            <span>Retry attempts: {retryCount}</span>
                        )}
                    </div>
                </div>
                <div>
                    <Button 
                        variant="outline-warning" 
                        size="sm"
                        onClick={checkServiceHealth}
                        disabled={serviceStatus === 'checking'}
                    >
                        <BsArrowClockwise className="me-1" />
                        Retry
                    </Button>
                </div>
            </div>
        </Alert>
    );

    const FallbackComponent = () => {
        if (fallbackComponent) {
            return fallbackComponent;
        }

        return (
            <div className="text-center py-5">
                <BsWifiOff className="text-muted display-4 mb-3" />
                <h5 className="text-muted mb-3">Reporting Service Unavailable</h5>
                <p className="text-muted mb-4">
                    The reporting service is temporarily unavailable. Please try again later.
                </p>
                <Button variant="primary" onClick={checkServiceHealth}>
                    <BsArrowClockwise className="me-2" />
                    Check Again
                </Button>
            </div>
        );
    };

    // Show status indicator if enabled
    const StatusIndicator = () => {
        if (!showStatus) return null;

        return (
            <div className="d-flex align-items-center justify-content-end mb-3">
                <small className="text-muted me-2">Reporting Service:</small>
                <div className="d-flex align-items-center">
                    {getStatusIcon()}
                    <span className="ms-2">{getStatusBadge()}</span>
                </div>
            </div>
        );
    };

    // If service is offline, show fallback UI
    if (serviceStatus === 'offline') {
        return (
            <>
                <StatusIndicator />
                <ServiceOfflineAlert />
                <FallbackComponent />
            </>
        );
    }

    // If service is online or checking, show children with status
    return (
        <>
            <StatusIndicator />
            {serviceStatus === 'offline' && <ServiceOfflineAlert />}
            {children}
        </>
    );
};

export default ServiceStatus;